import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { emptyArrayToUndefined } from "@formant/core/common/emptyArrayToUndefined";
import { toLegacyTagsets } from "@formant/core/common/toLegacyTagsets";
import { IFilter } from "@formant/core/model/IFilter";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { StreamType } from "@formant/core/model/StreamType";
import { Uuid } from "@formant/core/model/Uuid";
import { IconName } from "@formant/ui/component/common/Icon";

import { deviceDetailsStore, metadataStore } from "../../../stores";
import { AutocompleteField, IAutocompleteItem } from "../AutocompleteField";
import { streamTypeIcons } from "../StreamIcon";
import * as styles from "./FilterSearch.scss";

export interface IFilterSearchProps {
    filter: IFilter;
    context?: IScopeFilter;
    className?: string;
    onChange: (filter: IFilter) => void;
}

interface IDeviceOption {
    type: "device";
    deviceId: Uuid;
}

interface IStreamOption {
    type: "stream";
    name: string;
    streamType: StreamType;
}

interface ITypeOption {
    type: "type";
    streamType: StreamType;
}

interface ITagOption {
    type: "tag";
    key: string;
    value: string;
}

type FilterOption = IDeviceOption | IStreamOption | ITypeOption | ITagOption;

function filterOptionLabel(option: FilterOption): string {
    switch (option.type) {
        case "device": {
            const device = deviceDetailsStore.get(option.deviceId);
            return `Device: ${device ? device.name : ""}`;
        }
        case "stream":
            return `Stream: ${option.name}`;
        case "type":
            return `Type: ${option.streamType}`;
        case "tag":
            return `${option.key}: ${option.value}`;
    }
}

function filterOptionIcon(option: FilterOption): IconName {
    switch (option.type) {
        case "device":
            return "device";
        case "stream":
        case "type":
            return streamTypeIcons[option.streamType];
        case "tag":
            return "tags";
    }
}

function filterOptionSelected(option: FilterOption, filter: IFilter): boolean {
    switch (option.type) {
        case "device":
            return filter.deviceIds?.includes(option.deviceId) || false;
        case "stream":
            return filter.names?.includes(option.name) || false;
        case "type":
            return filter.types?.includes(option.streamType) || false;
        case "tag":
            return (
                (toLegacyTagsets(filter.tags) || {})[option.key] || []
            ).includes(option.value);
    }
}

@observer
export class FilterSearch extends Component<IFilterSearchProps> {
    public render() {
        const { filter, context, className } = this.props;
        const deviceIds = metadataStore.listDeviceIds(context || {}) || [];
        const streams = metadataStore.listStreams(context || {}) || {};
        const types = metadataStore.listStreamTypes(context || {}) || [];
        const tags = metadataStore.tagOptions(context || {}) || {};
        const options: FilterOption[] = [
            ...(deviceIds.length > 1
                ? deviceIds.map<IDeviceOption>(deviceId => ({
                      type: "device",
                      deviceId,
                  }))
                : []),
            ...(Object.keys(streams).length > 1
                ? Object.keys(streams).map<IStreamOption>(name => ({
                      type: "stream",
                      name,
                      streamType: streams[name],
                  }))
                : []),
            ...(types.length > 1
                ? types.map<ITypeOption>(type => ({
                      type: "type",
                      streamType: type,
                  }))
                : []),
            ...Object.keys(tags)
                .filter(key => !key.startsWith("$.") && tags[key].length > 1)
                .flatMap(key =>
                    tags[key].map<ITagOption>(value => ({
                        type: "tag",
                        key,
                        value,
                    })),
                ),
        ];
        if (options.length === 0) {
            return false;
        }
        const items = options.map<IAutocompleteItem<FilterOption>>(option => {
            return {
                value: option,
                icon: filterOptionIcon(option),
                label: filterOptionLabel(option),
                selected: filterOptionSelected(option, filter),
            };
        });
        return (
            <AutocompleteField
                label="Search filters"
                icon="search"
                collapsed={true}
                small={true}
                items={items}
                className={classnames(
                    styles["filter-search"],
                    styles.field,
                    className,
                )}
                onSelected={this.onSelected}
            />
        );
    }

    private onSelected = (option: FilterOption) => {
        const { filter, onChange } = this.props;
        if (option.type === "device") {
            const { deviceId } = option;
            const deviceIds = emptyArrayToUndefined(
                toggleValue(filter.deviceIds || [], deviceId),
            );
            onChange({
                ...filter,
                deviceIds,
            });
        } else if (option.type === "stream") {
            const { name } = option;
            const names = emptyArrayToUndefined(
                toggleValue(filter.names || [], name),
            );
            onChange({
                ...filter,
                names,
            });
        } else if (option.type === "type") {
            const { streamType } = option;
            const types = emptyArrayToUndefined(
                toggleValue(filter.types || [], streamType),
            );
            onChange({
                ...filter,
                types,
            });
        } else if (option.type === "tag") {
            const { key, value } = option;
            const values = emptyArrayToUndefined(
                toggleValue(
                    (toLegacyTagsets(filter.tags) || {})[key] || [],
                    value,
                ),
            );
            const tags =
                values !== undefined
                    ? { ...filter.tags, [key]: values }
                    : filter.tags;
            onChange({
                ...filter,
                tags: Object.keys(tags || {}).length > 0 ? tags : undefined,
            });
        }
    };
}

function toggleValue<T>(values: T[], value: T): T[] {
    return values.includes(value)
        ? values.filter(_ => _ !== value)
        : [...values, value];
}
